/* eslint-disable react-hooks/exhaustive-deps */
import BackButton from "components/BackButton";
import DirectorNotesButton from "components/DirectorNotesButton";
import {
  ActionButton,
  EmptyState,
  FileAttachment,
  getStatusChipColor,
  InfoCard,
  InfoRow,
} from "components/AppealUIComponents";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import BailRightDetailOnlyView from "pages/BailRight/BailRightDetailOnlyView";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import InstituteOfChargeDetail from "pages/InstituteOfCharge/InstituteOfChargeDetail";
import AppealRequestForm from "pages/InstituteOfCharge/modal/AppealRequestForm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import {
  FaBalanceScale,
  FaChartBar,
  FaFileAlt,
  FaSearch,
  FaSitemap,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import {
  editAppealRequest2,
  editHigherAdminFeedbackRequest2,
  fetchAppealRequestById,
  fetchCaseByProsecutorRegistratonNumber,
} from "services/appealService";
import {
  AppealRequest,
  AppealRequestStatus,
  AppealRequestType,
  FEEDBACK_MODEL,
  FILE_URL,
  HigherAdminFeedbackRequest,
  HigherAdminFeedbackRequestStatus,
  Roles,
  Suspect,
} from "types";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";
import AppealReport from "./AppealReport";
import AppealFeedbackSection from "./fragment/AppealFeedback";
import AppealRespondbackFeedback from "./fragment/AppealRespondbackFeedback";
import AssignProsecutorPanel from "./fragment/AssignProsecutorPanel";
import RegistrarCourtPanel from "./fragment/RegistrarCourtPanel";
import AppealEditForm from "./modal/AppealEditForm";
import EditHigherAdminFeedback from "./modal/EdittHigherAdminFeedback";
import HigherClerkForm from "./modal/HigherClerkForm";
import RegistrarHigherAdminFeedback from "./modal/RegistrarHigherAdminFeedback";
import RegistrarSubmittedToHigherAdmin from "./modal/RegistrarSubmittedToHigherAdmin";
import RequestHigherAdminFeedback from "./modal/RequestHigherAdminFeedback";
import RespondBackComment from "./modal/RespondBackComment";
import DocumentViewerModal from "components/DocumentViewer";
import { useCourtFilingMarks } from "components/CourtFilingMarks";

// react-icons' IconType isn't directly usable as JSX under this TS setup;
// render through a cast helper (same pattern used elsewhere in the app).
const BtnIcon: React.FC<{ icon: IconType }> = ({ icon }) =>
  React.createElement(icon as React.ComponentType<{ className?: string }>, {
    className: "w-4 h-4",
  });

type ActionTone = "default" | "primary" | "success" | "warning";

const ACTION_TONES: Record<ActionTone, string> = {
  default:
    "border-gray-200 text-gray-800 hover:border-gray-300 hover:bg-gray-50",
  primary: "border-red-200 text-red-700 hover:border-red-300 hover:bg-red-50",
  success:
    "border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50",
  warning:
    "border-amber-200 text-amber-700 hover:border-amber-300 hover:bg-amber-50",
};

// A single, immediately-visible action (replaces the hidden dropdown items).
const ActionRow: React.FC<{
  icon: string;
  label: string;
  tone: ActionTone;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, label, tone, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`group w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ACTION_TONES[tone]}`}
  >
    <span className="text-base leading-none">{icon}</span>
    <span className="flex-1 text-left">{label}</span>
    <svg
      className="w-4 h-4 opacity-30 group-hover:opacity-60 transition-opacity"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
);

const AppealDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { loginData } = useAuth();
  const navigate = useNavigate();
  const { showTooltip } = useTooltip();

  const [loading, setLoading] = React.useState(false);
  const [openRespondBack, setOpenRespondBack] = useState(false);
  const [openHigherClerk, setOpenHigherClerk] = useState(false);
  const [openRequestHigherAdmin, setOpenRequestHigherAdmin] = useState(false);
  const [selectedHighAdmin, setSelectedHighAdmin] =
    useState<HigherAdminFeedbackRequest>();
  const [openSubmitToHigherAdmin, setOpenSubmitToHigherAdmin] = useState(false);
  const [openHigherAdminFeedback, setOpenHigherAdminFeedback] = useState(false);
  const [openEditAppeal, setOpenEditAppeal] = useState(false);
  const [openEditHigherAdmin, setOpenEditHigherAdmin] = useState(false);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [action, setAction] = useState<"add" | "edit">();
  const [selectedCaseId, setSelectedCaseId] = useState<number>();
  const [selectedDefendant, setSelectedDefendant] = useState<Suspect>();
  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [activeSidebar, setActiveSidebar] = useState("normalFeedback");
  const [rightTab, setRightTab] = useState<"actions" | "feedback">("actions");
  const [activeMainPage, setActiveMainPage] = useState<
    "appeal" | "bailRight" | "higherAdmin" | "report"
  >("appeal");

  const [appeal, setAppeal] = useState<AppealRequest>();
  // The backend stores only requesting_prosecutor_id; resolve the name.
  const [requestingProsecutorName, setRequestingProsecutorName] = useState("");

  // The report view takes the full page (hide actions/feedback).
  const isFullPageView = activeMainPage === "report";

  const fetchData = () => {
    if (!id) return;

    setLoading(true);
    fetchAppealRequestById(id)
      .then((data) => {
        setAppeal(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Resolve the requesting prosecutor's name from its id (name not stored).
  // Use the appeal's own (requesting) admin/org so it resolves at any stage —
  // including after the appeal has moved to a higher unit and been assigned.
  useEffect(() => {
    if (
      !appeal ||
      appeal?.requesting_prosecutor ||
      !appeal?.requesting_prosecutor_id
    )
      return;
    const adminId =
      (appeal as any)?.previous_admin_unit_id ||
      appeal?.admin_unit_id ||
      loginData?.admin_unit;
    const orgId =
      (appeal as any)?.previous_org_unit_id ||
      appeal?.org_unit_id ||
      loginData?.org_unit;
    if (!adminId) return;
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((r) => r.json())
      .then((data) => {
        const match = (data || []).find(
          (e: any) =>
            String(e?.user?.id) === String(appeal?.requesting_prosecutor_id),
        );
        if (match)
          setRequestingProsecutorName(
            `${match.user?.first_name ?? ""} ${match.user?.last_name ?? ""}`.trim(),
          );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appeal, loginData]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  // Office stamp and signature printed on the appeal filed to court.
  const courtMarks = useCourtFilingMarks(
    appeal?.status === "FORWARDED_TO_REGISTRAR" &&
      isRegistrar &&
      !!appeal?.lower_court_type,
  );
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isVariousClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  const updateStatus = (
    status: keyof typeof AppealRequestStatus,
    coordinatorStatus = "",
  ) => {
    setActionIsLoading(true);
    editAppealRequest2({
      id: id,
      status: status,
      coordinator_status: coordinatorStatus
        ? coordinatorStatus
        : appeal?.coordinator_status,
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setActionIsLoading(false));
  };

  const updateHigherAdminStatus = (
    status: keyof typeof HigherAdminFeedbackRequestStatus,
    coordinatorStatus = "",
  ) => {
    setActionIsLoading(true);
    editHigherAdminFeedbackRequest2({
      id: selectedHighAdmin?.id,
      status: status,
      coordinator_status: coordinatorStatus
        ? coordinatorStatus
        : selectedHighAdmin?.coordinator_status,
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setActionIsLoading(false));
  };

  // Main header section
  const renderHeader = () => (
    <div className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      {/* Decorative gradient accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-700 via-red-600 to-red-500" />
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center shrink-0 shadow-md shadow-red-200">
            <span className="text-2xl text-white">⚖</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">
                {t(appeal?.is_cassation ? "cassation_case" : "appeal_case")}
              </h1>
              {appeal?.prosecutor_registration_number && (
                <span className="text-sm font-semibold text-gray-400">
                  #{appeal.prosecutor_registration_number}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {appeal?.parent_appeal && (
            <ActionButton
              tooltip={t("parent_appeal_tooltip")}
              onClick={() =>
                navigate("/prosecutor-appeal/" + appeal?.parent_appeal)
              }
            >
              <BtnIcon icon={FaSitemap} /> {t("parent_appeal")}
            </ActionButton>
          )}
          <ActionButton
            tooltip={t("investigation_tooltip")}
            onClick={async () => {
              try {
                fetchCaseByProsecutorRegistratonNumber(
                  appeal?.prosecutor_registration_number || "",
                ).then((data) => {
                  if (Array.isArray(data) && data?.length > 0) {
                    setSelectedCaseId(data[0]?.id);
                  }
                });
              } catch (error) {}
            }}
          >
            <BtnIcon icon={FaSearch} /> {t("investigation")}
          </ActionButton>
          {appeal?.bail_right && (
            <ActionButton
              tooltip={
                activeMainPage === "bailRight"
                  ? t("back_to_appeal_tooltip")
                  : t("bail_right_tooltip")
              }
              onClick={() => {
                setActiveMainPage(
                  activeMainPage === "bailRight" ? "appeal" : "bailRight",
                );
              }}
              variant={activeMainPage === "bailRight" ? "primary" : "outline"}
            >
              {activeMainPage === "bailRight" ? (
                <>
                  <BtnIcon icon={FaFileAlt} />{" "}
                  {t(appeal?.is_cassation ? "cassation" : "appeal")}
                </>
              ) : (
                <>
                  <BtnIcon icon={FaBalanceScale} /> {t("bail_right")}
                </>
              )}
            </ActionButton>
          )}
          {appeal?.status === "ASSIGNED_TO_PROSECUTOR" && (
            <ActionButton
              tooltip={
                activeMainPage === "report"
                  ? t("back_to_appeal_tooltip")
                  : t("report_tooltip")
              }
              onClick={() => {
                setActiveMainPage(
                  activeMainPage === "report" ? "appeal" : "report",
                );
              }}
              variant={activeMainPage === "report" ? "primary" : "outline"}
            >
              {activeMainPage === "report" ? (
                <>
                  <BtnIcon icon={FaFileAlt} />{" "}
                  {t(appeal?.is_cassation ? "cassation" : "appeal")}
                </>
              ) : (
                <>
                  <BtnIcon icon={FaChartBar} /> {t("report")}
                </>
              )}
            </ActionButton>
          )}
        </div>
      </div>

    </div>
  );

  // Main appeal content
  const renderAppealContent = () => (
    <div className="space-y-4">
      <InfoCard title={t(appeal?.is_cassation ? "cassation_request" : "appeal_request")} defaultOpen={true}>
        {/* Highlighted assignment / registrar details */}
        {(appeal?.requesting_prosecutor ||
          requestingProsecutorName ||
          appeal?.assigned_prosecutor ||
          appeal?.registration_number ||
          appeal?.registration_date) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[
              {
                icon: "📨",
                label: t("requesting_prosecutor", {
                  defaultValue: "Requesting Prosecutor",
                }),
                value: appeal?.requesting_prosecutor || requestingProsecutorName,
              },
              {
                icon: "👤",
                label: t("assigned_prosecutor"),
                value: appeal?.assigned_prosecutor,
              },
              {
                icon: "🔖",
                label: t("registrar_number"),
                value: appeal?.registration_number,
              },
              {
                icon: "📅",
                label: t("registrar_date"),
                value: appeal?.registration_date
                  ? DateView(appeal.registration_date)
                  : "",
              },
            ]
              .filter((f) => f.value)
              .map((f, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-red-100 bg-red-50/50 p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base leading-none">{f.icon}</span>
                    <p className="text-[11px] uppercase tracking-wide text-red-700/80 font-semibold">
                      {f.label}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {f.value}
                  </p>
                </div>
              ))}
          </div>
        )}

        <div className="space-y-1">
          {[
            {
              label: t("incident_registration_number"),
              value: appeal?.incident_registration_number ?? "-",
            },
            {
              label: t("prosecutor_registration_number"),
              value: appeal?.prosecutor_registration_number ?? "-",
            },
            {
              label: t("crime_reference_number"),
              value: appeal?.crime_registration_number ?? "-",
            },
            {
              label: t("court_registration_number"),
              value: appeal?.court_registration_number ?? "-",
            },
            {
              label: t("appeal_requested_date"),
              value: appeal?.created_at
                ? new Date(appeal.created_at).toLocaleDateString()
                : "-",
            },
            {
              label: t(appeal?.is_cassation ? "cassation" : "appeal_type"),
              value: appeal?.appeal_type
                ? t(`appeal_type_${appeal.appeal_type.toLowerCase()}`)
                : "-",
            },
            { label: t("crime_type"), value: appeal?.crime_type || "-" },
            {
              label: t("defendants_name"),
              value: appeal?.defendent_name_if_bail_right || "-",
            },
          ].map(
            (item, index) =>
              item.value !== "-" && (
                <InfoRow key={index} label={item.label} value={item.value} />
              ),
          )}
        </div>
      </InfoCard>

      <InfoCard title={t("lower_court_information")}>
        {appeal?.lower_court_type ? (
          <div className="space-y-1">
            {[
              { label: t("lower_court_type"), value: appeal?.lower_court_type },
              {
                label: t("lower_court_level"),
                value: appeal?.lower_court_level,
              },
              {
                label: t("lower_registration_date"),
                value: appeal?.lower_registration_date
                  ? DateView(appeal?.lower_registration_date)
                  : "-",
              },
              {
                label: t("lower_registration_number"),
                value: appeal?.lower_registration_number,
              },
              { label: t("lower_court_name"), value: appeal?.lower_court_name },
              { label: t("lower_court_zone"), value: appeal?.lower_court_zone },
              {
                label: t("lower_court_woreda"),
                value: appeal?.lower_court_woreda,
              },
            ].map(
              (item, index) =>
                item.value && (
                  <InfoRow key={index} label={item.label} value={item.value} />
                ),
            )}
          </div>
        ) : (
          <EmptyState message={t("no_lower_court_information")} />
        )}
      </InfoCard>

      <InfoCard title={t("higher_court_information")}>
        {appeal?.higher_court_type ? (
          <div className="space-y-1">
            {[
              {
                label: t("higher_court_type"),
                value: appeal?.higher_court_type,
              },
              {
                label: t("higher_court_level"),
                value: appeal?.higher_court_level,
              },
              {
                label: t("higher_court_name"),
                value: appeal?.higher_court_name,
              },
              {
                label: t("higher_court_zone"),
                value: appeal?.higher_court_zone,
              },
              {
                label: t("higher_court_woreda"),
                value: appeal?.higher_court_woreda,
              },
              {
                label: t("higher_court_hearing_date"),
                value: appeal?.higher_court_hearing_date
                  ? DateView(appeal?.higher_court_hearing_date)
                  : "-",
              },
              {
                label: t("registration_number"),
                value: appeal?.registration_number,
              },
              {
                label: t("registration_date"),
                value: appeal?.registration_date
                  ? DateView(appeal?.registration_date)
                  : "-",
              },
              {
                label: t("court_appeal_number"),
                value: appeal?.court_appeal_number,
              },
              {
                label: t("opening_date"),
                value: appeal ? DateView(appeal?.opening_date) : "-",
              },
              {
                label: t("first_hearing_date"),
                value: appeal?.first_hearidng_date
                  ? DateView(appeal?.first_hearidng_date)
                  : "-",
              },
            ].map(
              (item, index) =>
                item.value &&
                item.value !== "-" && (
                  <InfoRow key={index} label={item.label} value={item.value} />
                ),
            )}
          </div>
        ) : (
          <EmptyState message={t("no_higher_court_information")} />
        )}
      </InfoCard>

      {appeal?.defendants && appeal.defendants.length > 0 && (
        <InfoCard title={t("defendants")} badgeCount={appeal.defendants.length}>
          <div className="space-y-3">
            {appeal.defendants.map((defendant: any, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-white transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-sm font-semibold uppercase">
                    {(defendant.defendant_info?.first_name?.[0] || "") +
                      (defendant.defendant_info?.last_name?.[0] || "") || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {defendant.defendant_info?.first_name}{" "}
                      {defendant.defendant_info?.middle_name}{" "}
                      {defendant.defendant_info?.last_name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {[
                        defendant.defendant_info?.gender,
                        defendant.defendant_info?.phone_number,
                        defendant.defendant_info?.statement,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDefendant(defendant.defendant_info)}
                  className="p-2 shrink-0 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t("view_defendant_details")}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      <InfoCard title={t("summary_attachments")}>
        <div className="space-y-4">
          {appeal?.appeal_summary_text && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                {t(appeal?.is_cassation ? "cassation_complain_summary" : "appeal_complain_summary")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {appeal.appeal_summary_text}
              </p>
            </div>
          )}

          {appeal?.reason && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t("reason")}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {appeal.reason}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <DocumentViewerModal
              documents={(appeal?.documents || []) as any}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.APPEAL_NORMAL_FEEDBACK}`}
              layout="list"
              hideEmptyFile={true}
            />
          </div>
        </div>
      </InfoCard>

      {appeal?.id && (
        <DirectorNotesButton
          model={FEEDBACK_MODEL.prosecutorAppeal}
          objectId={appeal.id}
          role={
            isProsecutor
              ? "prosecutor"
              : isRegistrar
                ? "registrar"
                : isCoordinator || isAppealCoordinator
                  ? "coordinator"
                  : undefined
          }
        />
      )}
    </div>
  );

  // Available appeal actions for the current status/role, as data so we can
  // show them inline, count them, and render an empty state when there are none.
  const isHigherClerk =
    isVariousClerk || isEconomicClerk || isCorruptionClerk;
  const isAppealCoordinator =
    isCorruptionAppealCoordinator ||
    isEconomicAppealCoordinator ||
    isVariousAppealCoordinator;
  const isProsecutorAppealEditable =
    isProsecutor &&
    (appeal?.status === "PENDING" ||
      appeal?.status === "COORDINATOR_RESPONDED_BACK");

  const appealActions: {
    key: string;
    show?: boolean;
    icon: string;
    label: string;
    tone: ActionTone;
    onClick: () => void;
  }[] = [
    {
      key: "forward_to_coordinator",
      show: isProsecutorAppealEditable,
      icon: "📤",
      tone: "primary",
      label: t("forward_to_coordinator"),
      onClick: () => updateStatus("FORWARDED_TO_COORDINATOR", "PENDING"),
    },
    {
      key: "edit_appeal",
      show: isProsecutorAppealEditable,
      icon: "✏️",
      tone: "default",
      label: t(
        appeal?.is_cassation ? "edit_cassation_request" : "edit_appeal_request",
      ),
      onClick: () => setOpenEditAppeal(true),
    },
    {
      key: "request_higher_admin",
      show:
        isProsecutorAppealEditable &&
        !!loginData?.structure?.admin_unit?.parent_unit,
      icon: "📋",
      tone: "default",
      label: t("request_higher_admin_feedback"),
      onClick: () => setOpenRequestHigherAdmin(true),
    },
    {
      key: "respond_back",
      show:
        appeal?.status === "FORWARDED_TO_COORDINATOR" &&
        !appeal?.higher_court_type &&
        isCoordinator,
      icon: "↩️",
      tone: "warning",
      label: t("respond_back_to_prosecutor"),
      onClick: () => setOpenRespondBack(true),
    },
    {
      key: "approve",
      show:
        appeal?.status === "FORWARDED_TO_COORDINATOR" &&
        !appeal?.higher_court_type &&
        isCoordinator,
      icon: "✅",
      tone: "success",
      label: t("approve"),
      onClick: () => updateStatus("FORWARDED_TO_REGISTRAR", "PENDING"),
    },
    // Lower court info is handled by an inline panel (see Case Actions tab).
    {
      key: "send_to_court",
      show:
        appeal?.status === "FORWARDED_TO_REGISTRAR" &&
        isRegistrar &&
        !!appeal?.lower_court_type,
      icon: "🏛️",
      tone: "primary",
      label: t("send_to_court"),
      onClick: () => {
        editAppealRequest2(
          {
          id: id,
          status: "SUBMITTED_TO_COURT",
          admin_unit_id: loginData?.structure?.admin_unit?.parent_unit
            ? loginData?.structure?.admin_unit?.parent_unit || ""
            : (appeal?.admin_unit_id ?? null),
          org_unit_id: loginData?.structure?.admin_unit?.parent_unit
            ? ""
            : (appeal?.org_unit_id ?? null),
          previous_admin_unit_id: loginData?.structure?.admin_unit?.parent_unit
            ? appeal?.admin_unit_id || ""
            : "",
          previous_org_unit_id: loginData?.structure?.admin_unit?.parent_unit
            ? appeal?.org_unit_id || ""
            : "",
          },
          courtMarks.marks,
        )
          .then(() => {
            showTooltip(t("successful"), "success");
            navigate("/appeal");
          })
          .catch((error) => {
            showTooltip(
              error?.response?.data?.detail || errorHandlerOnForm(error),
              "error",
            );
          });
      },
    },
    // Higher court info is handled by an inline panel (see Case Actions tab).
    {
      key: "forward_to_higher_coordinator",
      show:
        appeal?.status === "SUBMITTED_TO_COURT" &&
        isRegistrar &&
        !!appeal?.higher_court_type,
      icon: "📤",
      tone: "primary",
      label: t("forward_to_higher_coordinator"),
      onClick: () =>
        updateStatus(
          "FORWARDED_TO_HIGHER_COORDINATOR",
          appeal?.coordinator_status,
        ),
    },
    {
      key: "add_org_unit",
      show:
        appeal?.status === "FORWARDED_TO_HIGHER_CLERK" &&
        isHigherClerk &&
        !appeal?.org_unit_id,
      icon: "➕",
      tone: "default",
      label: t("add_organizational_unit"),
      onClick: () => {
        setOpenHigherClerk(true);
        setAction("add");
      },
    },
    {
      key: "edit_org_unit",
      show:
        appeal?.status === "FORWARDED_TO_HIGHER_CLERK" &&
        isHigherClerk &&
        !!appeal?.org_unit_id,
      icon: "✏️",
      tone: "default",
      label: t("edit_organizational_unit"),
      onClick: () => {
        setOpenHigherClerk(true);
        setAction("edit");
      },
    },
    {
      key: "clerk_forward_to_coordinator",
      show:
        appeal?.status === "FORWARDED_TO_HIGHER_CLERK" &&
        isHigherClerk &&
        !!appeal?.org_unit_id,
      icon: "📤",
      tone: "primary",
      label: t("forward_to_coordinator"),
      onClick: () =>
        updateStatus(
          "FORWARDED_TO_HIGHER_COORDINATOR",
          appeal?.coordinator_status,
        ),
    },
  ];

  // Appeal coordinator can assign (when forwarded to them) or reassign (once
  // already assigned). Rendered as an inline panel in the Actions tab.
  const canAssignProsecutor =
    isAppealCoordinator &&
    !!appeal?.higher_court_type &&
    (appeal?.status === "FORWARDED_TO_HIGHER_COORDINATOR" ||
      appeal?.status === "ASSIGNED_TO_PROSECUTOR");

  // Registrar court-info panels (inline, replacing the modals).
  const showLowerCourtPanel =
    appeal?.status === "FORWARDED_TO_REGISTRAR" && isRegistrar;
  const showHigherCourtPanel =
    appeal?.status === "SUBMITTED_TO_COURT" && isRegistrar;

  const lowerCourtFields = [
    { name: "lower_court_type", label: t("lower_court_type"), required: true },
    { name: "lower_court_level", label: t("lower_court_level"), required: true },
    {
      name: "lower_registration_number",
      label: t("lower_registration_number"),
      required: true,
    },
    { name: "lower_court_name", label: t("lower_court_name") },
    { name: "lower_court_zone", label: t("lower_court_zone") },
    { name: "lower_court_woreda", label: t("lower_court_woreda") },
    {
      name: "lower_registration_date",
      label: t("lower_registration_date"),
      type: "date" as const,
      required: true,
    },
  ];

  const higherCourtFields = [
    { name: "higher_court_type", label: t("higher_court_type"), required: true },
    {
      name: "higher_court_level",
      label: t("higher_court_level"),
      required: true,
    },
    { name: "higher_court_name", label: t("higher_court_name") },
    { name: "higher_court_zone", label: t("higher_court_zone") },
    { name: "higher_court_woreda", label: t("higher_court_woreda") },
    {
      name: "higher_court_hearing_date",
      label: t("higher_court_hearing_date"),
      type: "date" as const,
    },
    { name: "registration_number", label: t("registrar_number") },
    {
      name: "registration_date",
      label: t("registrar_date"),
      type: "date" as const,
    },
    { name: "court_appeal_number", label: t("court_appeal_number") },
    { name: "opening_date", label: t("opening_date"), type: "date" as const },
    {
      name: "first_hearidng_date",
      label: t("first_hearing_date"),
      type: "date" as const,
    },
  ];

  // Available higher-admin-feedback actions for the selected request.
  const isHighAdminProsecutorEditable =
    isProsecutor &&
    (selectedHighAdmin?.status === "PENDING" ||
      selectedHighAdmin?.status === "RESPONDED_BACK_TO_PROSECUTOR");

  const higherAdminActions: {
    key: string;
    show?: boolean;
    icon: string;
    label: string;
    tone: ActionTone;
    onClick: () => void;
  }[] = [
    {
      key: "forward_to_coordinator",
      show: isHighAdminProsecutorEditable,
      icon: "📤",
      tone: "primary",
      label: t("forward_to_coordinator"),
      onClick: () =>
        updateHigherAdminStatus("FORWARDED_TO_COORDINATOR", "PENDING"),
    },
    {
      key: "edit_request",
      show: isHighAdminProsecutorEditable,
      icon: "✏️",
      tone: "default",
      label: t("edit_request"),
      onClick: () => setOpenEditHigherAdmin(true),
    },
    {
      key: "approve",
      show:
        selectedHighAdmin?.status === "FORWARDED_TO_COORDINATOR" &&
        selectedHighAdmin?.higher_coordinator_status === "PENDING" &&
        isCoordinator,
      icon: "✅",
      tone: "success",
      label: t("approve"),
      onClick: () =>
        updateHigherAdminStatus("COORDINATOR_APPROVED", "PENDING"),
    },
    {
      key: "forward_to_registrar",
      show: selectedHighAdmin?.status === "COORDINATOR_APPROVED" && isCoordinator,
      icon: "📤",
      tone: "primary",
      label: t("forward_to_registrar"),
      onClick: () =>
        updateHigherAdminStatus("FORWARDED_TO_REGISTRAR", "PENDING"),
    },
    {
      key: "forward_to_higher_admin_registrar",
      show:
        selectedHighAdmin?.status === "FORWARDED_TO_REGISTRAR" &&
        !selectedHighAdmin?.registrar_number &&
        isRegistrar,
      icon: "📋",
      tone: "default",
      label: t("forward_to_higher_admin_registrar"),
      onClick: () => setOpenSubmitToHigherAdmin(true),
    },
    {
      key: "higher_admin_feedback",
      show:
        selectedHighAdmin?.status === "FORWARDED_TO_REGISTRAR" &&
        !!selectedHighAdmin?.registrar_number &&
        isRegistrar,
      icon: "💬",
      tone: "default",
      label: t("higher_admin_feedback"),
      onClick: () => setOpenHigherAdminFeedback(true),
    },
    {
      key: "complete",
      show:
        selectedHighAdmin?.status === "ASSIGNED_TO_PROSECUTOR" && isProsecutor,
      icon: "✅",
      tone: "success",
      label: t("complete"),
      onClick: () => updateHigherAdminStatus("COMPLETED"),
    },
  ];

  // The action set shown in the right column depends on the active main page.
  const visibleActions = (
    activeMainPage === "higherAdmin" && selectedHighAdmin
      ? higherAdminActions
      : activeMainPage === "appeal"
      ? appealActions
      : []
  ).filter((a) => a.show);

  // Higher Admin Feedback Section
  const renderHigherAdminFeedback = () => (
    <div className="space-y-4">
      {appeal?.higher_admin_feedbacks?.map((feedback: any, index: number) => (
        <div
          key={index}
          className={`bg-white rounded-xl shadow-sm border p-6 transition-all ${
            selectedHighAdmin?.id === feedback?.id
              ? "ring-2 ring-red-500 border-red-300"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          {feedback?.status === "COORDINATOR_RESPOND_BACK" && (
            <div className="flex justify-end mb-4">
              <ActionButton
                onClick={() => {
                  setSelectedHighAdmin(feedback);
                  setOpenRequestHigherAdmin(true);
                }}
                variant="primary"
                className="text-sm"
              >
                ✏️ {t("edit")}
              </ActionButton>
            </div>
          )}

          <div
            className="cursor-pointer"
            onClick={() => {
              setSelectedHighAdmin(
                selectedHighAdmin?.id === feedback?.id ? undefined : feedback,
              );
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {[
                {
                  label: t("requested_by"),
                  value: feedback?.requested_by ?? "-",
                },
                {
                  label: t("registrar_number"),
                  value: feedback?.registrar_number ?? "-",
                },
                {
                  label: t("registrar_date"),
                  value: feedback?.registrar_date
                    ? DateView(feedback?.registrar_date)
                    : "-",
                },
                {
                  label: t("assigned_prosecutor"),
                  value: feedback?.assigned_prosecutor ?? "-",
                },
                {
                  label: t("higher_admin_feedback_date"),
                  value: feedback?.higher_admin_feedback_date
                    ? new Date(
                        feedback.higher_admin_feedback_date,
                      ).toLocaleDateString()
                    : "-",
                },
                {
                  label: t("status"),
                  value: feedback?.status
                    ? HigherAdminFeedbackRequestStatus[
                        feedback.status as keyof typeof HigherAdminFeedbackRequestStatus
                      ]
                    : "-",
                },
              ].map(
                (item, idx) =>
                  item.value !== "-" && (
                    <div key={idx} className="flex flex-col">
                      <span className="text-sm font-medium text-gray-600">
                        {item.label}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 mt-1">
                        {item.label === t("status") ? (
                          <span className={`${getStatusChipColor(item.value)}`}>
                            {item.value}
                          </span>
                        ) : (
                          item.value
                        )}
                      </span>
                    </div>
                  ),
              )}
            </div>

            <div className="space-y-4">
              {feedback?.request_summary && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {t("request_summary")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {feedback.request_summary}
                  </p>
                </div>
              )}

              {feedback?.higher_admin_feedback && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {t("higher_admin_feedback")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {feedback.higher_admin_feedback}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {feedback?.request_file && (
                  <FileAttachment
                    name={t("request_file")}
                    url={feedback.request_file}
                  />
                )}
                {feedback?.higher_admin_feedback_file && (
                  <FileAttachment
                    name={t("higher_admin_feedback_file")}
                    url={feedback.higher_admin_feedback_file}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      {(!appeal?.higher_admin_feedbacks ||
        appeal.higher_admin_feedbacks.length === 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <EmptyState message={t("no_higher_admin_feedbacks")} />
        </div>
      )}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:h-full flex flex-col bg-gray-50 py-4">
      <div className="px-4 sm:px-6 lg:px-8 flex flex-col flex-1 lg:min-h-0">
        {/* Back Button */}
        {selectedCaseId && (
          <div className="mb-4 shrink-0">
            <BackButton
              onClick={() => {
                setActiveMainPage("appeal");
                setSelectedCaseId(undefined);
              }}
            />
          </div>
        )}
        {!selectedCaseId ? (
          <div className="flex flex-col flex-1 lg:min-h-0">
            <div className="shrink-0">{renderHeader()}</div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:flex-1 lg:min-h-0">
              {/* Left Column - Appeal Content (full width in report view) */}
              <div
                className={`space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1 ${
                  isFullPageView ? "lg:col-span-2" : ""
                }`}
              >
                {activeMainPage === "appeal" && renderAppealContent()}
                {activeMainPage === "bailRight" &&
                  appeal?.bail_right?.uu_id && (
                    <BailRightDetailOnlyView
                      bailRightId={appeal.bail_right.uu_id}
                    />
                  )}
                {activeMainPage === "higherAdmin" &&
                  renderHigherAdminFeedback()}
                {activeMainPage === "report" && appeal && (
                  <AppealReport
                    pre_judgement_entries={appeal.pre_judgement_entries}
                    daily_report={appeal.daily_report || []}
                    appeal_final_verdict={appeal.appeal_final_verdict || []}
                    appealData={appeal}
                    fetchData={fetchData}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator || isAppealCoordinator}
                  />
                )}
              </div>

              {/* Right Column - Actions & Feedback (hidden in report view) */}
              <div
                className={`space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1 ${
                  isFullPageView ? "hidden" : ""
                }`}
              >
                {/* Actions & Feedback — one tabbed panel */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Top tabs */}
                  <div className="flex border-b border-gray-200">
                    {[
                      {
                        key: "actions" as const,
                        label: t("case_actions"),
                        badge: visibleActions.length,
                      },
                      {
                        key: "feedback" as const,
                        label: t("feedback"),
                        badge: 0,
                      },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setRightTab(tab.key)}
                        className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                          rightTab === tab.key
                            ? "text-red-700"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {tab.label}
                        {tab.badge > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 bg-red-50 text-red-700 text-xs font-semibold rounded-full border border-red-100">
                            {tab.badge}
                          </span>
                        )}
                        {rightTab === tab.key && (
                          <span className="absolute bottom-0 inset-x-0 h-0.5 bg-red-600" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Actions tab */}
                  {rightTab === "actions" && (
                    <div className="p-6 space-y-4">
                      {actionIsLoading ? (
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                          {t("processing")}
                        </div>
                      ) : (
                        <>
                          {visibleActions.length > 0 && (
                            <div className="space-y-2">
                              {visibleActions.map((a) => (
                                <ActionRow
                                  key={a.key}
                                  icon={a.icon}
                                  label={a.label}
                                  tone={a.tone}
                                  disabled={actionIsLoading}
                                  onClick={a.onClick}
                                />
                              ))}
                            </div>
                          )}

                          {/* Inline assign / reassign prosecutor */}
                          {canAssignProsecutor && appeal?.id && (
                            <AssignProsecutorPanel
                              appealId={appeal.id}
                              assignedProsecutor={appeal?.assigned_prosecutor}
                              onAssigned={fetchData}
                            />
                          )}

                          {showLowerCourtPanel && (
                            <RegistrarCourtPanel
                              appeal={appeal}
                              icon="⚖️"
                              title={t("lower_court_information")}
                              fields={lowerCourtFields}
                              onSaved={fetchData}
                            />
                          )}

                          {courtMarks.fields && (
                            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
                              <h4 className="text-sm font-semibold text-gray-900">
                                🔏 {t("court_stamp_and_signature")}
                              </h4>
                              {courtMarks.fields}
                            </div>
                          )}

                          {showHigherCourtPanel && (
                            <RegistrarCourtPanel
                              appeal={appeal}
                              icon="🏛️"
                              title={t("higher_court_information")}
                              fields={higherCourtFields}
                              onSaved={fetchData}
                            />
                          )}

                          {visibleActions.length === 0 &&
                            !canAssignProsecutor &&
                            !showLowerCourtPanel &&
                            !showHigherCourtPanel && (
                              <EmptyState
                                message={t("no_actions_available")}
                              />
                            )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Feedback tab */}
                  {rightTab === "feedback" && (
                    <div className="p-6 space-y-4">
                      <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                        {[
                          { key: "normalFeedback", label: t("normal_feedback") },
                          {
                            key: "respondBack",
                            label: t("respond_back_feedback"),
                          },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveSidebar(tab.key)}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              activeSidebar === tab.key
                                ? "bg-white text-gray-900 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      <div>
                        {activeSidebar === "normalFeedback" && appeal?.id && (
                          <AppealFeedbackSection
                            appealId={appeal.id}
                            normal_feedbacks={appeal.normal_feedbacks || []}
                            fetchData={fetchData}
                          />
                        )}
                        {activeSidebar === "respondBack" && (
                          <AppealRespondbackFeedback
                            normal_feedbacks={
                              appeal?.respondback_feedbacks || []
                            }
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 lg:min-h-0 lg:overflow-y-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <InstituteOfChargeDetail caseId={selectedCaseId} />
            </div>
          </div>
        )}

        {/* Modals */}
        {selectedDefendant && (
          <SuspectDetailInfo
            suspect={selectedDefendant}
            isOpen={!!selectedDefendant}
            isDefendant={true}
            onClose={() => setSelectedDefendant(undefined)}
          />
        )}

        {openEditAppeal && appeal && (
          <AppealEditForm
            appeal={appeal}
            onClose={(refetch) => {
              setOpenEditAppeal(false);
              if (refetch) fetchData();
            }}
          />
        )}

        {openAppealForm && appeal && (
          <AppealRequestForm
            appeal={appeal}
            appealType={
              appeal?.appeal_type as "PRE_JUDGEMENT" | "FINAL_VERDICT"
            }
            onClose={() => setOpenAppealForm(false)}
          />
        )}

        {/* Higher Admin Modals */}
        {appeal?.id && openRequestHigherAdmin && (
          <RequestHigherAdminFeedback
            appealId={selectedHighAdmin ? undefined : appeal?.id}
            crime_type={appeal?.crime_type}
            higherAdminFeedbackRequest={selectedHighAdmin}
            onClose={(refetch) => {
              setOpenRequestHigherAdmin(false);
              if (refetch) fetchData();
            }}
          />
        )}

        {selectedHighAdmin && openEditHigherAdmin && (
          <EditHigherAdminFeedback
            higherAdminFeedbackRequest={selectedHighAdmin}
            onClose={(refetch) => {
              setOpenEditHigherAdmin(false);
              if (refetch) fetchData();
            }}
          />
        )}

        {appeal?.id && openRespondBack && (
          <RespondBackComment
            appealId={selectedHighAdmin ? undefined : appeal?.id}
            higherAdminFeedbackId={selectedHighAdmin?.id}
            respondBackTo={
              selectedHighAdmin?.status === "FORWARDED_TO_HIGHER_COORDINATOR"
                ? "FORWARDED_TO_REGISTRAR"
                : "COORDINATOR_RESPOND_BACK"
            }
            onClose={(refetch) => {
              setOpenRespondBack(false);
              if (refetch) fetchData();
            }}
            type={"prosecutor"}
          />
        )}


        {appeal && openHigherClerk && action && (
          <HigherClerkForm
            appeal={appeal}
            action={action}
            onClose={(refetch) => {
              setOpenHigherClerk(false);
              if (refetch) fetchData();
            }}
          />
        )}

        {selectedHighAdmin?.id && openSubmitToHigherAdmin && (
          <RegistrarSubmittedToHigherAdmin
            feedbackId={selectedHighAdmin?.id}
            higherAdmin={selectedHighAdmin}
            onClose={(refetch) => {
              setOpenSubmitToHigherAdmin(false);
              if (refetch) fetchData();
            }}
          />
        )}

        {selectedHighAdmin?.id && openHigherAdminFeedback && (
          <RegistrarHigherAdminFeedback
            feedbackId={selectedHighAdmin?.id}
            feedback={selectedHighAdmin}
            onClose={(refetch) => {
              setOpenHigherAdminFeedback(false);
              if (refetch) fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AppealDetail;
